import { useCallback, useEffect, useMemo, useState } from 'react';
import { createEmpty, extend as extendExtent } from 'ol/extent';
import * as XLSX from 'xlsx';
import { normalizeRecord, toMapPoint } from 'features/geocoder/utils/coordinates';

const HISTORY_HEADERS = [
    'No',
    'Mode',
    'Input',
    'Address Type',
    'SRID',
    'X',
    'Y',
    'X3857',
    'Y3857',
    'Road Address',
    'Jibun Address',
    'Timestamp',
    'Status',
];

const GEOCODER_POINT_STYLE = { fill: '#2563eb', stroke: '#ffffff', radius: 8, opacity: 0.8 };
const DEFAULT_LAYER_STYLES = {
    point: { ...GEOCODER_POINT_STYLE },
    line: { stroke: '#2563eb', width: 3, opacity: 1 },
    polygon: { fill: '#60a5fa', stroke: '#1d4ed8', width: 2, opacity: 0.85 },
};

const buildExtentFromRecords = (records = []) => {
    const extent = createEmpty();
    let hasExtent = false;
    records.forEach((record) => {
        const x = Number(record.x3857);
        const y = Number(record.y3857);
        if (!Number.isFinite(x) || !Number.isFinite(y) || (x === 0 && y === 0)) {
            return;
        }
        extendExtent(extent, [x, y, x, y]);
        hasExtent = true;
    });
    return hasExtent ? extent : null;
};

const rebuildLayerWithRecords = (layer, records) => ({
    ...layer,
    records,
    geojson: {
        type: 'FeatureCollection',
        features: records.map((record) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [record.x3857, record.y3857] },
            properties: record,
        })),
    },
});

export default function useGeocoderAppState() {
    const [panelMode, setPanelMode] = useState('1');
    const [activeTab, setActiveTab] = useState('layer');
    const [layers, setLayers] = useState([]);
    const [selectedLayerIndex, setSelectedLayerIndex] = useState(null);
    const [isAttributeTableExpanded, setIsAttributeTableExpanded] = useState(false);
    const [focusPoint, setFocusPoint] = useState(null);
    const [archivedHistory, setArchivedHistory] = useState([]);
    const [pendingFitExtent, setPendingFitExtent] = useState(null);
    const [fileAttributeRecords, setFileAttributeRecords] = useState([]);

    useEffect(() => {
        setLayers((prev) =>
            prev.map((layer) => {
                if (layer?.group !== 'Geocoder') return layer;
                const nextStyles = {
                    ...layer.styles,
                    point: {
                        ...GEOCODER_POINT_STYLE,
                        ...(layer.styles?.point || {}),
                        fill: GEOCODER_POINT_STYLE.fill,
                        stroke: GEOCODER_POINT_STYLE.stroke,
                        opacity: GEOCODER_POINT_STYLE.opacity,
                        radius: layer.styles?.point?.radius ?? GEOCODER_POINT_STYLE.radius,
                    },
                };
                return { ...layer, styles: nextStyles };
            })
        );
    }, [setLayers]);

    const addLayerFromRecord = useCallback(
        (record, mapPointOverride = null) => {
            const mapPoint = mapPointOverride ?? toMapPoint(record);
            if (!mapPoint) return;

            if (record.source === 'manual' && record.addrType) {
                if (String(record.addrType).startsWith('road')) {
                    record.roadAddress = record.input;
                } else if (String(record.addrType).startsWith('jibun')) {
                    record.jibunAddress = record.input;
                }
            }

            const enrichedRecord = normalizeRecord(record, mapPoint);
            if (!enrichedRecord) return;
            const layer = {
                id: enrichedRecord.id,
                name: enrichedRecord.input || (enrichedRecord.mode === 'forward' ? '주소 검색' : '좌표 검색'),
                geojson: {
                    type: 'FeatureCollection',
                    features: [
                        {
                            type: 'Feature',
                            geometry: { type: 'Point', coordinates: [mapPoint.x, mapPoint.y] },
                            properties: { ...enrichedRecord },
                        },
                    ],
                },
                records: [enrichedRecord],
                visible: true,
                group: 'Geocoder',
                styles: DEFAULT_LAYER_STYLES,
                clusterDistance: 0,
                meta: {
                    source: 'manual',
                    input: enrichedRecord.input,
                    coordSystem: enrichedRecord.srid,
                    geocodingType: enrichedRecord.addrType,
                    timestamp: enrichedRecord.timestamp,
                    fileName: enrichedRecord.fileName ?? null,
                },
            };
            setLayers((prev) => {
                const nextLayer = layer.name ? layer : { ...layer, name: `지오코딩 결과 ${prev.length + 1}` };
                const next = [...prev, nextLayer];
                setSelectedLayerIndex(next.length - 1);
                return next;
            });
            setFocusPoint([mapPoint.x, mapPoint.y]);
        },
        [toMapPoint]
    );

    const addLayerFromGeoJSON = useCallback((geojson, layerName, layerMeta = null) => {
        if (!geojson || !Array.isArray(geojson.features)) return;
        const createdAt = Date.now();
        const features = [];
        const records = [];
        const attributeRows = [];
        geojson.features.forEach((feature, idx) => {
            if (!feature || feature.type !== 'Feature') return;
            const geometry = feature.geometry;
            if (!geometry || geometry.type !== 'Point') return;
            const coords = geometry.coordinates || [];
            const x = Number(coords[0]);
            const y = Number(coords[1]);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return;
            const props = feature.properties || {};
            const sridSource =
                props.srid ??
                layerMeta?.coordSystem?.code ??
                layerMeta?.coordSystem ??
                null;
            const sridNormalized = sridSource
                ? String(sridSource).replace(/epsg:/i, '')
                : null;
            const candidateRecord = {
                id: props.id ?? `${createdAt}-${idx}`,
                mode: props.mode ?? 'batch',
                srid: sridNormalized ? `EPSG:${sridNormalized}` : null,
                input: props.input ?? props.address ?? '',
                addrType: props.addrType ?? '',
                roadAddress: props.roadAddress ?? props.road_address ?? '',
                jibunAddress: props.jibunAddress ?? props.jibun_address ?? '',
                timestamp: props.timestamp ?? props.createdAt ?? null,
                x: props.x ?? coords[0],
                y: props.y ?? coords[1],
                x3857: props.x3857 ?? undefined,
                y3857: props.y3857 ?? undefined,
                fileName: props.fileName ?? layerMeta?.fileName ?? null,
                source: props.source ?? layerMeta?.source ?? 'file',
            };
            const mapPoint = toMapPoint(candidateRecord);
            if (!mapPoint) return;
            const normalized = normalizeRecord(candidateRecord, mapPoint);
            if (!normalized) return;
            attributeRows.push({
                id: normalized.id,
                __rowIndex: idx + 1,
                ...feature.properties,
                x3857: normalized.x3857,
                y3857: normalized.y3857,
                mapX: normalized.mapX,
                mapY: normalized.mapY,
            });
            records.push(normalized);
            features.push({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [normalized.x3857, normalized.y3857] },
                properties: normalized,
            });
        });
        if (!records.length) return;
        const layerExtent = buildExtentFromRecords(records);
        const nextLayer = {
            id: createdAt,
            name: layerName || `지오코딩 결과 ${records.length}`,
            geojson: { type: 'FeatureCollection', features },
            records,
            visible: true,
            group: 'Geocoder',
            styles: DEFAULT_LAYER_STYLES,
            clusterDistance: 0,
            meta: layerMeta
                ? { source: 'file', ...layerMeta }
                : {
                      source: 'file',
                      fileName: layerName || `batch-${createdAt}`,
                      coordSystem: { code: '', label: '' },
                  },
        };
        setLayers((prev) => {
            const layerWithName = nextLayer.name ? nextLayer : { ...nextLayer, name: `지오코딩 결과 ${prev.length + 1}` };
            const next = [...prev, layerWithName];
            setSelectedLayerIndex(next.length - 1);
            return next;
        });
        const lastRecord = records[records.length - 1];
        if (Number.isFinite(lastRecord.x3857) && Number.isFinite(lastRecord.y3857)) {
            setFocusPoint([lastRecord.x3857, lastRecord.y3857]);
        }
        if (layerExtent) {
            setPendingFitExtent(layerExtent);
        }
        setFileAttributeRecords(attributeRows);
    }, []);

    const removeRecordFromLayer = useCallback(
        (layerIndex, recordId) => {
            if (layerIndex == null || recordId == null) return;
            setLayers((prev) => {
                const targetLayer = prev[layerIndex];
                if (!targetLayer) return prev;
                const nextRecords = (targetLayer.records ?? []).filter((record) => record.id !== recordId);
                if (nextRecords.length === (targetLayer.records ?? []).length) {
                    return prev;
                }
                if (!nextRecords.length) {
                    const next = prev.filter((_, idx) => idx !== layerIndex);
                    setSelectedLayerIndex((current) => {
                        if (current == null) return current;
                        if (current === layerIndex) return null;
                        if (current > layerIndex) return current - 1;
                        return current;
                    });
                    return next;
                }
                const updatedLayer = rebuildLayerWithRecords(targetLayer, nextRecords);
                const next = [...prev];
                next[layerIndex] = updatedLayer;
                return next;
            });
        },
        [setSelectedLayerIndex]
    );

    const handleResultRecord = useCallback(
        (record, mapX, mapY) => {
            const hasMapPoint = Number.isFinite(mapX) && Number.isFinite(mapY);
            const override = hasMapPoint ? { x: mapX, y: mapY } : null;
            if (hasMapPoint) {
                setFocusPoint([mapX, mapY]);
            }
            addLayerFromRecord(record, override);
        },
        [addLayerFromRecord]
    );

    const layerHistoryRecords = useMemo(
        () =>
            layers.flatMap((layer, layerIndex) =>
                (layer.records ?? []).map((record) => ({
                    ...record,
                    layerIndex,
                    layerId: layer.id,
                    layerName: layer.name,
                    archived: false,
                    source: record.source ?? layer.meta?.source ?? 'manual',
                    fileName: record.fileName ?? layer.meta?.fileName ?? null,
                }))
            ),
        [layers]
    );

    const historyRecords = useMemo(
        () => [
            ...layerHistoryRecords,
            ...archivedHistory.map((record) => ({
                ...record,
                archived: true,
            })),
        ],
        [archivedHistory, layerHistoryRecords]
    );

    const historyGeojson = useMemo(() => {
        const features = historyRecords
            .map((record, idx) => {
                const x = Number(record.x3857 ?? record.mapX ?? record.x);
                const y = Number(record.y3857 ?? record.mapY ?? record.y);
                if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
                return {
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: [x, y] },
                    properties: { ...record, id: record.id ?? idx },
                };
            })
            .filter(Boolean);
        return {
            type: 'FeatureCollection',
            features,
        };
    }, [historyRecords]);

    const manualHistoryRecords = useMemo(
        () => historyRecords.filter((record) => record.source === 'manual'),
        [historyRecords]
    );
    const fileHistoryRecords = useMemo(
        () => historyRecords.filter((record) => record.source === 'file'),
        [historyRecords]
    );

    const manualAttributeRecords = useMemo(
        () => manualHistoryRecords.filter((record) => !record.archived),
        [manualHistoryRecords]
    );

    const fileHistoryMeta = useMemo(
        () =>
            layers
                .map((layer, layerIndex) => {
                    if (layer.meta?.source !== 'file') return null;
                    const records = layer.records ?? [];
                    const sampleRecord = records[records.length - 1] || null;
                    return {
                        key: layer.id ?? layerIndex,
                        fileName: layer.meta?.fileName || layer.name || `파일 업로드 ${layerIndex + 1}`,
                        recordsCount: records.length,
                        geocodingType: layer.meta?.geocodingType || sampleRecord?.addrType || '',
                        coordSystem:
                            layer.meta?.coordSystem?.label ||
                            layer.meta?.coordSystem?.code ||
                            sampleRecord?.srid ||
                            '',
                        timestamp: layer.meta?.timestamp || sampleRecord?.timestamp || '',
                        layerIndex,
                        layerId: layer.id,
                        sampleRecord: sampleRecord ? { ...sampleRecord, layerIndex, layerId: layer.id } : null,
                    };
                })
                .filter(Boolean),
        [layers]
    );

    const historyMatrix = useMemo(
        () =>
            historyRecords.map((record, idx) => [
                idx + 1,
                record.mode,
                record.input,
                record.addrType || '',
                record.srid || '',
                record.x ?? '',
                record.y ?? '',
                record.x3857 ?? '',
                record.y3857 ?? '',
                record.roadAddress || '',
                record.jibunAddress || '',
                record.timestamp || '',
                record.archived ? 'Archived' : 'Active',
            ]),
        [historyRecords]
    );

    const saveBlob = useCallback((blob, filename) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }, []);

    const downloadHistoryCSV = useCallback(() => {
        if (!historyMatrix.length) return;
        const formatCell = (value) => {
            if (value == null) return '';
            const str = String(value);
            const escaped = str.replace(/"/g, '""');
            return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        };
        const csvLines = [
            HISTORY_HEADERS.join(','),
            ...historyMatrix.map((row) => row.map(formatCell).join(',')),
        ];
        const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        saveBlob(blob, `geocode_history_${Date.now()}.csv`);
    }, [historyMatrix, saveBlob]);

    const downloadHistoryXLSX = useCallback(() => {
        if (!historyMatrix.length) return;
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([HISTORY_HEADERS, ...historyMatrix]);
        XLSX.utils.book_append_sheet(wb, ws, 'History');
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveBlob(blob, `geocode_history_${Date.now()}.xlsx`);
    }, [historyMatrix, saveBlob]);

    const downloadHistoryGeoJSON = useCallback(() => {
        if (!historyRecords.length) return;
        const blob = new Blob([JSON.stringify(historyGeojson, null, 2)], {
            type: 'application/geo+json',
        });
        saveBlob(blob, `geocode_history_${Date.now()}.geojson`);
    }, [historyGeojson, historyRecords.length, saveBlob]);

    const downloadOptions = useMemo(() => {
        if (!historyRecords.length) return [];
        return [
            { value: 'csv', label: 'CSV 형식', action: downloadHistoryCSV },
            { value: 'xlsx', label: 'XLSX 형식', action: downloadHistoryXLSX },
            { value: 'geojson', label: 'GeoJSON 형식', action: downloadHistoryGeoJSON },
        ];
    }, [historyRecords.length, downloadHistoryCSV, downloadHistoryXLSX, downloadHistoryGeoJSON]);

    const handleHistoryFavoriteToggle = useCallback((record) => {
        if (!record) return;
        if (record.archived) {
            setArchivedHistory((prev) =>
                prev.map((item) => (item.id === record.id ? { ...item, favorite: !item.favorite } : item))
            );
            return;
        }
        if (!record.layerId) return;
        setLayers((prev) =>
            prev.map((layer) => {
                if (layer.id !== record.layerId) return layer;
                const nextRecords = (layer.records ?? []).map((r) =>
                    r.id === record.id ? { ...r, favorite: !r.favorite } : r
                );
                return rebuildLayerWithRecords(layer, nextRecords);
            })
        );
    }, []);

    const handleHistoryArchive = useCallback(
        (record) => {
            if (!record) return;
            const archivedCopy = {
                ...record,
                archived: true,
                archivedAt: new Date().toISOString(),
                layerIndex: null,
                layerId: null,
            };
            setArchivedHistory((prev) => {
                const filtered = prev.filter((item) => item.id !== archivedCopy.id);
                return [archivedCopy, ...filtered];
            });
            if (!record.archived) {
                removeRecordFromLayer(record.layerIndex, record.id);
            }
        },
        [removeRecordFromLayer]
    );

    const handleHistoryRestore = useCallback(
        (record) => {
            if (!record?.archived) return;
            setArchivedHistory((prev) => prev.filter((item) => item.id !== record.id));
            addLayerFromRecord(record);
        },
        [addLayerFromRecord]
    );

    const handleHistoryDelete = useCallback(
        (record) => {
            if (!record) return;
            if (record.archived) {
                setArchivedHistory((prev) => prev.filter((item) => item.id !== record.id));
                return;
            }
            removeRecordFromLayer(record.layerIndex, record.id);
        },
        [removeRecordFromLayer]
    );

    const handleHistoryRowSelect = useCallback(
        (obj) => {
            const record = obj.record ?? obj;
            if (!record || typeof record !== 'object') return;

            console.log("handleHistoryRowSelect, layerIndex = ", record.layerIndex)
            if (record.layerIndex != null) {
                setSelectedLayerIndex(record.layerIndex);
            }
            setIsAttributeTableExpanded(true);
            const x = Number(record.x3857 ?? record.mapX ?? record.x);
            const y = Number(record.y3857 ?? record.mapY ?? record.y);
            if (Number.isFinite(x) && Number.isFinite(y)) {
                setFocusPoint([x, y]);
            }
            // console.log("selectedLayerIndex = ", selectedLayerIndex)
            // console.log("isAttributeTableExpanded = ", isAttributeTableExpanded)
            // console.log("focusPoint = ", focusPoint)
        },
        [setSelectedLayerIndex, setIsAttributeTableExpanded, setFocusPoint]
    );

    const handleTableExpand = useCallback((shouldExpanded) => {
        setIsAttributeTableExpanded(shouldExpanded);
    }, [setIsAttributeTableExpanded]);

    useEffect(() => {
        console.log("✨ 상태 업데이트 후 - selectedLayerIndex:", selectedLayerIndex);
    }, [selectedLayerIndex]);

    useEffect(() => {
        console.log("✨ 상태 업데이트 후 - isAttributeTableExpanded:", isAttributeTableExpanded);
    }, [isAttributeTableExpanded]);

    useEffect(() => {
        console.log("✨ 상태 업데이트 후 - focusPoint:", focusPoint);
    }, [focusPoint]);

    const clearFileLayers = useCallback(() => {
        setLayers((prev) => prev.filter((layer) => layer.meta?.source !== 'file'));
        setFileAttributeRecords([]);
        setFocusPoint(null);
    }, [setLayers, setFileAttributeRecords, setFocusPoint]);

    const updateRecordInLayer = useCallback((updatedRecord) => {
        if (!updatedRecord || updatedRecord.layerIndex == null || updatedRecord.id == null) return;

        const mapPoint = toMapPoint(updatedRecord);
        if (!mapPoint) {
            console.error("Failed to convert coordinates, cannot update record.", updatedRecord);
            return;
        }

        const newRecord = normalizeRecord(updatedRecord, mapPoint);

        setLayers(prev => {
            const { layerIndex, id } = newRecord;
            const targetLayer = prev[layerIndex];
            if (!targetLayer) return prev;

            const nextRecords = (targetLayer.records || []).map(record =>
                record.id === id ? newRecord : record
            );
            
            const updatedLayer = rebuildLayerWithRecords(targetLayer, nextRecords);

            const nextLayers = [...prev];
            nextLayers[layerIndex] = updatedLayer;
            return nextLayers;
        });
    }, [setLayers]);

    return {
        panelMode,
        setPanelMode,
        activeTab,
        setActiveTab,
        layers,
        selectedLayerIndex,
        setSelectedLayerIndex,
        isAttributeTableExpanded,
        // setIsAttributeTableExpanded,
        handleTableExpand,
        focusPoint,
        historyRecords,
        manualHistoryRecords,
        fileHistoryRecords,
        fileHistoryMeta,
        manualAttributeRecords,
        fileAttributeRecords,
        historyGeojson,
        pendingFitExtent,
        downloadOptions,
        handleHistoryRowSelect,
        handleHistoryFavoriteToggle,
        handleHistoryArchive,
        handleHistoryRestore,
        handleHistoryDelete,
        handleResultRecord,
        addLayerFromGeoJSON,
        consumeFitExtent: () => setPendingFitExtent(null),
        clearFileLayers,
        updateRecordInLayer,
    };
}
